// Turning a pointer drag into a reordering, live.
//
// The trace view's drag doesn't wait for a drop. The moment the pointer has
// travelled more than halfway towards the nearest position the dragged step
// could occupy, the program re-runs under a schedule that puts it there and the
// row snaps into place; keep pulling and it snaps again. So the gesture needs
// geometry the drop-based version never did — where each legal position *is* on
// screen, measured in rows — and this module is the pure half of that. Nothing
// here touches the DOM. See docs/design/trace-reordering.md.
//
// Everything is measured against the run the gesture started on (its
// **baseline**), never the run currently displayed. The displayed run changes on
// every snap, and if the slots moved with it, how far you had to pull for the
// next snap would depend on the snap that just happened — the drag would fight
// back, and dragging straight back to where you started wouldn't undo. Fixing
// the geometry for the life of the gesture makes pointer distance mean the same
// thing from the first pixel to the last.

import { canDrop, stepGroups } from "./reorder.js";
import type { Causality } from "../src/backend/interp/causality.js";

/** A position the dragged step can occupy, as a place to land plus how far the
 *  rows travel to get there. */
export interface Slot {
  /** The step it lands on, indexed into the baseline run's steps. */
  target: number;
  /** Which side of `target` the dragged step ends up on. */
  side: "before" | "after";
  /** How far the dragged rows travel to reach this slot, in rows — negative up,
   *  positive down, and zero for the slot the step already occupies. */
  offset: number;
}

/**
 * Every position `moved` could legally occupy, in trace order, including the
 * one it already occupies (`offset === 0`).
 *
 * Only *visible* steps are candidates: a step that emitted no rows has no
 * position on screen to aim at, and lands where its visible neighbour does. The
 * walk stops at the first step `canDrop` refuses in each direction, which is the
 * same contiguity `dropRange` relies on — asking `canDrop` directly keeps this
 * honest rather than assuming it.
 */
export function dropSlots(c: Causality, moved: number): Slot[] {
  const groups = stepGroups(c);
  const self = groups.findIndex((g) => g.step === moved);
  if (self < 0) return [];

  const out: Slot[] = [];
  let up = 0;
  for (let i = self - 1; i >= 0 && canDrop(c, moved, groups[i]!.step); i--) {
    const g = groups[i]!;
    up += g.endRow - g.firstRow;
    out.unshift({ target: g.step, side: "before", offset: -up });
  }
  out.push({ target: moved, side: "before", offset: 0 });
  let down = 0;
  for (let i = self + 1; i < groups.length && canDrop(c, moved, groups[i]!.step); i++) {
    const g = groups[i]!;
    down += g.endRow - g.firstRow;
    out.push({ target: g.step, side: "after", offset: down });
  }
  return out;
}

/**
 * Which slot a pointer that has moved `dy` pixels is asking for: the one whose
 * offset it is nearest to, which puts the boundary between two neighbouring
 * slots exactly halfway between them.
 *
 * Ties go to staying put — the rule is *more* than halfway — and the slot the
 * gesture is already in (`current`) gets `HYSTERESIS` pixels of grip on top of
 * that. Without the grip, a hand held on a boundary crosses it a pixel at a
 * time, and since every crossing re-runs the program, the trace would flicker
 * between two schedules for as long as the hand hovered there. The grip is
 * small enough that it reads as the boundary being where the arithmetic says.
 */
export function slotFor(
  slots: readonly Slot[],
  dy: number,
  rowHeight: number,
  current = -1
): number {
  // Doubled: moving the pointer one pixel past a midpoint shortens the distance
  // to the far slot by one and lengthens the distance to the near one by one, so
  // a `2 * HYSTERESIS` discount is `HYSTERESIS` pixels of pointer travel.
  const grip = (i: number): number => (i === current ? 2 * HYSTERESIS : 0);
  let best = slots.findIndex((s) => s.offset === 0);
  if (best < 0) return -1;
  let bestDist = Math.abs(dy) - grip(best);
  slots.forEach((s, i) => {
    const dist = Math.abs(s.offset * rowHeight - dy) - grip(i);
    if (dist < bestDist) {
      bestDist = dist;
      best = i;
    }
  });
  return best;
}

/** How far past halfway the pointer must travel to leave the slot it is already
 *  in, in pixels. */
export const HYSTERESIS = 3;

/**
 * The lean of the picked-up rows: how far the pointer has travelled since the
 * gesture last settled — the drag beginning, or the most recent snap — damped
 * and clamped to a few pixels.
 *
 * Measured from the last settling rather than from the slot's own position, so
 * the lean is zero right after a snap and grows again towards the next one. (The
 * distance to the slot itself is the wrong quantity: slots are whole steps, so a
 * snap usually moves the rows further than the pointer travelled, and the rows
 * would lurch back at full stretch the instant they landed.)
 *
 * Damped and clamped, because the rows must *not* follow the pointer: a step
 * that slid smoothly would promise it could be dropped anywhere, when in fact it
 * can only ever be in one of `slots`. A few pixels of give says "held, and
 * pulling this way" without making that promise.
 */
export function pullOffset(dySinceSettled: number, max = 5): number {
  return Math.max(-max, Math.min(max, dySinceSettled * 0.35));
}
